import { useNavigate } from '@tanstack/react-router';
import { Link } from '../../components/Link';
import { PlatformMark } from '../../components/PlatformMark';
import { Sparkline } from '../../components/charts/Sparkline';
import { SortableTable, type Column } from '../../components/SortableTable';
import { fmtMs, fmtReq, pctFine, usdCost } from '../../lib/format';
import { quotedRateLabel, quotedRateUsd } from '../../lib/pricing';
import type { ProviderTapeKey, ProviderTapeRow, ProviderTapeSort } from './providerTape';

const dash = <span className="text-text-3">-</span>;

const Head = ({ children }: { children: string }) => (
  <span className="mono text-10 uppercase tracking-widest">{children}</span>
);

const Price = ({ row }: { row: ProviderTapeRow }) => (
  <span className={`mono shrink-0 whitespace-nowrap text-13 ${row.price.metered ? 'text-meter' : 'text-accent'}`}>
    {usdCost(quotedRateUsd(row.price))}
    <span className="ml-1 text-10 text-text-3">{quotedRateLabel(row.price)}</span>
  </span>
);

/** The name is a real anchor so the page can be crawled and focused by
 *  keyboard; its click is stopped so the row handler does not navigate twice. */
const EndpointName = ({ row }: { row: ProviderTapeRow }) => (
  <span className="flex min-w-0 flex-col">
    <Link
      to={row.href}
      onClick={(event) => event.stopPropagation()}
      className="truncate text-13 font-medium text-text underline-offset-2 group-hover:text-accent group-hover:underline"
    >
      {row.name}
    </Link>
    <span className="mono truncate text-11 text-text-3">{row.sku}</span>
  </span>
);

const columns: Column<ProviderTapeRow, ProviderTapeKey>[] = [
  {
    key: 'rank',
    header: <Head>{'#'}</Head>,
    sortable: true,
    className: 'w-20 pl-6 pr-0 text-right sm:pl-8 lg:pl-10',
    cell: (row) => <span className="mono text-12 text-text-3">{row.rank}</span>,
  },
  {
    key: 'name',
    header: <Head>{'Endpoint'}</Head>,
    sortable: true,
    cell: (row) => (
      <div className="flex items-center gap-3">
        <PlatformMark id={row.platformId} icon={row.icon} category={row.category} size={18} bare />
        <EndpointName row={row} />
      </div>
    ),
  },
  {
    key: 'daily',
    header: <Head>{'30 days'}</Head>,
    className: 'w-44 max-s980:hidden',
    cell: (row) => (
      <Sparkline
        values={row.daily}
        width={132}
        height={24}
        dot
        aria-label={`${row.name} daily requests`}
      />
    ),
  },
  {
    key: 'requests',
    header: <Head>{'Requests'}</Head>,
    sortable: true,
    numeric: true,
    className: 'w-28',
    cell: (row) => <span className="mono text-13 text-text">{row.requests > 0 ? fmtReq(row.requests) : dash}</span>,
  },
  {
    key: 'success',
    header: <Head>{'Success'}</Head>,
    sortable: true,
    numeric: true,
    className: 'w-24 max-s820:hidden',
    cell: (row) => (
      <span className="mono text-13 text-text-2">{row.successPct === null ? dash : pctFine(row.successPct)}</span>
    ),
  },
  {
    key: 'p50',
    header: <Head>{'Typical'}</Head>,
    sortable: true,
    numeric: true,
    className: 'w-24 max-s820:hidden',
    cell: (row) => (
      <span className="mono text-13 text-text-2">{row.p50Ms === null ? dash : fmtMs(row.p50Ms)}</span>
    ),
  },
  {
    key: 'price',
    header: <Head>{'Price'}</Head>,
    sortable: true,
    numeric: true,
    className: 'w-36 pr-6 sm:pr-8 lg:pr-10',
    cell: (row) => <Price row={row} />,
  },
];

/** Below 720px the table would need a sideways scroll, so each endpoint is one
 *  stacked record instead, carrying the same figures in reading order. */
const ProviderTapeCards = ({ rows }: { rows: ProviderTapeRow[] }) => (
  <ul className="border-t border-line-2">
    {rows.map((row) => (
      <li key={row.sku} className="border-b border-line-2 last:border-b-0">
        <Link to={row.href} className="flex gap-3 px-6 py-4 sm:px-8">
          <span className="mono w-6 shrink-0 pt-0.5 text-right text-11 text-text-3">{row.rank}</span>
          <PlatformMark id={row.platformId} icon={row.icon} category={row.category} size={18} bare />
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-3">
              <span className="truncate text-13 font-medium text-text">{row.name}</span>
              <span className="mono shrink-0 text-13 text-text">{row.requests > 0 ? fmtReq(row.requests) : '-'}</span>
            </span>
            <span className="mono mt-1 flex items-baseline justify-between gap-3 text-11">
              <span className="truncate text-text-3">{row.sku}</span>
              <Price row={row} />
            </span>
            <span className="mono mt-2 flex flex-wrap gap-x-4 text-11 text-text-3">
              <span>{row.successPct === null ? '-' : pctFine(row.successPct)} success</span>
              <span>{row.p50Ms === null ? '-' : fmtMs(row.p50Ms)} typical</span>
            </span>
          </span>
        </Link>
      </li>
    ))}
  </ul>
);

export interface ProviderTapeTableProps {
  rows: ProviderTapeRow[];
  sort: ProviderTapeSort;
  onSort: (key: ProviderTapeKey) => void;
}

/** Every endpoint the provider stands behind, ranked, each row a way into it. */
export const ProviderTapeTable = ({ rows, sort, onSort }: ProviderTapeTableProps) => {
  const navigate = useNavigate();
  const hrefOf = new Map(rows.map((row) => [row.sku, row.href]));
  return (
    <>
      <div className="s720:hidden">
        <ProviderTapeCards rows={rows} />
      </div>
      <div className="max-s720:hidden">
        <SortableTable
          columns={columns}
          rows={rows}
          rowId={(row) => row.sku}
          sort={sort}
          onSort={onSort}
          onRowClick={(sku) => void navigate({ to: hrefOf.get(sku) ?? '/providers' })}
        />
      </div>
    </>
  );
};
